// src/constants/theme.ts

export type RoddoTheme = {
  appBg: string;
  appBgAlt: string;
  surface: string;
  surfaceElevated: string;
  surfaceSoft: string;
  panel: string;
  panelStrong: string;
  border: string;
  borderStrong: string;
  text: string;
  textSoft: string;
  textMuted: string;
  shadow: string;
  shadowStrong: string;
  glass: string;
  glassHighlight: string;
  header: string;
  dock: string;
  accent: string;
  accentSoft: string;
  accentText: string;
  chip: string;

  rewardGold: string;
  rewardGoldSoft: string;
  rewardGoldText: string;
  rewardMint: string;
  rewardMintSoft: string;
  rewardMintText: string;
  rewardBlue: string;
  rewardBlueSoft: string;
  rewardBlueText: string;
  rewardPlum: string;
  rewardPlumSoft: string;
  rewardPlumText: string;

  loreTitle: string;
  loreSub: string;
};

export type RoddoUiTokens = {
  isDark: boolean;
  pageBg?: string;
  surfaceBg: string;
  surfaceBgElevated: string;
  surfaceBgSoft: string;
  cardBg: string;
  cardBgStrong: string;
  cardBgMuted: string;
  menuBg: string;
  inputBg: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  border: string;
  borderStrong: string;
  shadow: string;
  shadowStrong: string;
  insetHighlight: string;
  tabMutedBg: string;
  tabMutedBorder: string;
  overlaySoft: string;
};

export const LIGHT_THEME: RoddoTheme = {
  appBg: '#f8efe4',
  appBgAlt: '#f4e6d8',
  surface: '#f8efe7',
  surfaceElevated: 'rgba(255,250,242,0.94)',
  surfaceSoft: 'rgba(255,248,239,0.88)',
  panel: 'rgba(255,249,240,0.92)',
  panelStrong: 'rgba(255,250,243,0.97)',
  border: 'rgba(79,53,31,0.09)',
  borderStrong: 'rgba(79,53,31,0.14)',
  text: '#22313A',
  textSoft: 'rgba(34,49,58,0.84)',
  textMuted: 'rgba(78,68,60,0.70)',
  shadow: '0 12px 28px rgba(98,71,45,0.08)',
  shadowStrong: '0 18px 42px rgba(98,71,45,0.12)',
  glass: 'rgba(255,249,240,0.72)',
  glassHighlight: 'rgba(255,255,255,0.58)',
  header: 'rgba(249,242,235,0.94)',
  dock: 'rgba(248,240,232,0.96)',
  accent: '#7c6ff7',
  accentSoft: 'rgba(124,111,247,0.14)',
  accentText: '#5242B6',
  chip: 'rgba(255,250,242,0.90)',

  rewardGold: '#f6c453',
  rewardGoldSoft: '#fff0cb',
  rewardGoldText: '#7b5a00',
  rewardMint: '#86efac',
  rewardMintSoft: '#dff6ea',
  rewardMintText: '#17684c',
  rewardBlue: '#7cc7ff',
  rewardBlueSoft: '#dff1ff',
  rewardBlueText: '#0d5b8a',
  rewardPlum: '#c4b5fd',
  rewardPlumSoft: '#efe3ff',
  rewardPlumText: '#5b2e91',

  loreTitle: '#22313A',
  loreSub: '#655C53',
};

export const RODDO_NIGHT = {
  // Reference-aligned night system:
  // navy owns structure; purple is reserved for active/progression states.
  canvas: '#070B15',
  shell: '#0A1120',
  surface: '#101827',
  raised: '#151F31',
  control: '#0C1423',
  border: 'rgba(132,151,190,0.17)',
  borderStrong: 'rgba(142,158,199,0.27)',
  text: '#F2F6FC',
  textSecondary: '#A8B4C7',
  textMuted: '#74839A',
  purple: '#8A70F2',
  purpleSoft: 'rgba(138,112,242,0.12)',
  aqua: '#67D8E8',
  mint: '#66D6A9',
  gold: '#F2B84B',
  coral: '#EF6A73',
} as const;

export const DARK_THEME: RoddoTheme = {
  appBg: RODDO_NIGHT.canvas,
  appBgAlt: RODDO_NIGHT.shell,
  surface: RODDO_NIGHT.surface,
  surfaceElevated: RODDO_NIGHT.raised,
  surfaceSoft: '#111A2A',
  panel: 'rgba(16,24,39,0.97)',
  panelStrong: 'rgba(21,31,49,0.985)',
  border: RODDO_NIGHT.border,
  borderStrong: RODDO_NIGHT.borderStrong,
  text: RODDO_NIGHT.text,
  textSoft: RODDO_NIGHT.textSecondary,
  textMuted: RODDO_NIGHT.textMuted,
  shadow: '0 10px 24px rgba(0,0,0,0.24)',
  shadowStrong: '0 16px 38px rgba(0,0,0,0.34)',
  glass: 'rgba(10,17,32,0.88)',
  glassHighlight: 'rgba(255,255,255,0.055)',
  header: 'rgba(10,17,32,0.97)',
  dock: 'rgba(10,17,32,0.985)',
  accent: RODDO_NIGHT.purple,
  accentSoft: RODDO_NIGHT.purpleSoft,
  accentText: '#D9D2FF',
  chip: 'rgba(255,255,255,0.055)',

  rewardGold: RODDO_NIGHT.gold,
  rewardGoldSoft: 'rgba(242,184,75,0.15)',
  rewardGoldText: '#F8D88E',
  rewardMint: RODDO_NIGHT.mint,
  rewardMintSoft: 'rgba(102,214,169,0.14)',
  rewardMintText: '#BDEED9',
  rewardBlue: RODDO_NIGHT.aqua,
  rewardBlueSoft: 'rgba(102,217,232,0.14)',
  rewardBlueText: '#BDEFF5',
  rewardPlum: '#9C8CFF',
  rewardPlumSoft: RODDO_NIGHT.purpleSoft,
  rewardPlumText: '#D9D2FF',

  loreTitle: RODDO_NIGHT.text,
  loreSub: RODDO_NIGHT.textSecondary,
};

export const getRoddoTheme = (isDarkMode: boolean): RoddoTheme =>
  isDarkMode ? DARK_THEME : LIGHT_THEME;

export const getRoddoUiTokens = (isDarkMode: boolean): RoddoUiTokens => {
  const theme = getRoddoTheme(isDarkMode);
  return {
    isDark: isDarkMode,
    pageBg: isDarkMode
      ? 'linear-gradient(180deg, #0A1120 0%, #070B15 100%)'
      : 'linear-gradient(180deg, #faf3e9 0%, #f5e9dd 100%)',
    surfaceBg: isDarkMode
      ? 'rgba(16,24,39,0.98)'
      : 'linear-gradient(180deg, rgba(249,243,236,0.98), rgba(244,236,228,0.99))',
    surfaceBgElevated: isDarkMode
      ? 'rgba(21,31,49,0.985)'
      : 'linear-gradient(180deg, rgba(255,250,243,0.99), rgba(249,240,230,0.99))',
    surfaceBgSoft: isDarkMode
      ? 'rgba(17,26,42,0.97)'
      : 'linear-gradient(180deg, rgba(255,248,239,0.97), rgba(247,238,227,0.98))',
    cardBg: isDarkMode
      ? 'rgba(17,26,42,0.97)'
      : 'rgba(255,250,243,0.97)',
    cardBgStrong: isDarkMode
      ? 'rgba(21,31,49,0.985)'
      : 'rgba(255,253,249,0.98)',
    cardBgMuted: isDarkMode
      ? 'rgba(12,20,35,0.96)'
      : 'rgba(250,244,238,0.92)',
    menuBg: isDarkMode
      ? 'rgba(8,14,27,0.99)'
      : 'rgba(253,248,243,0.98)',
    inputBg: isDarkMode ? 'rgba(12,20,35,0.98)' : 'rgba(255,250,244,0.96)',
    textPrimary: theme.text,
    textSecondary: theme.textSoft,
    textMuted: theme.textMuted,
    border: theme.border,
    borderStrong: theme.borderStrong,
    shadow: theme.shadow,
    shadowStrong: theme.shadowStrong,
    insetHighlight: isDarkMode
      ? 'inset 0 1px 0 rgba(255,255,255,0.055)'
      : 'inset 0 1px 0 rgba(255,255,255,0.68)',
    tabMutedBg: isDarkMode ? 'rgba(138,112,242,0.055)' : 'rgba(255,255,255,0.66)',
    tabMutedBorder: isDarkMode ? 'rgba(132,151,190,0.17)' : 'rgba(79,53,31,0.10)',
    overlaySoft: isDarkMode ? 'rgba(156,173,208,0.055)' : 'rgba(255,255,255,0.58)',
  };
};

export const resolveRoddoDarkMode = (explicit?: boolean): boolean => {
  if (typeof explicit === 'boolean') return explicit;
  if (typeof document === 'undefined') return false;

  const root = document.documentElement;
  const datasetTheme = root.dataset.theme;
  if (datasetTheme === 'dark') return true;
  if (datasetTheme === 'light') return false;

  return root.classList.contains('dark');
};

export const roddoText = {
  title: 'text-[#22313A] dark:text-[#F4F6FA]',
  body: 'text-[#5F5A52] dark:text-[#AEB8C9]',
  muted: 'text-[#766E63] dark:text-[#7F8BA0]',
  chip: 'text-[#3B4652] dark:text-[#DCE3EE]',
  loreTitle: 'text-[#22313A] dark:text-[#F4F6FA]',
  loreSub: 'text-[#6B6258] dark:text-[#AEB8C9]',
  reward: 'text-[#7B5A00] dark:text-[#F8D88E]',
  signal: 'text-[#0D5B8A] dark:text-[#BDEFF5]',
  success: 'text-[#17684C] dark:text-[#BDEED9]',
};

export const roddoSurfaceClass = (isDarkMode: boolean) =>
  isDarkMode
    ? 'bg-[#0A1120] text-[#F2F6FC]'
    : 'bg-[#f8efe7] text-[#22313A]';

export const roddoCardClass = (isDarkMode: boolean) =>
  isDarkMode
    ? 'bg-[#101827] border-[rgba(132,151,190,0.17)] text-[#F2F6FC]'
    : 'bg-[rgba(255,255,255,0.76)] border-black/10 text-[#22313A]';

export const roddoGlassCardClass =
  'relative isolate overflow-hidden rounded-[28px] border border-black/10 bg-[#fffaf2]/90 text-[#22313A] shadow-[0_16px_42px_rgba(89,65,37,0.12),inset_0_1px_0_rgba(255,255,255,0.60)] backdrop-blur-2xl before:pointer-events-none before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/24 before:via-white/10 before:to-transparent after:pointer-events-none after:absolute after:inset-0 after:bg-[radial-gradient(120%_80%_at_20%_0%,rgba(255,255,255,0.18),transparent_55%)] dark:border-[rgba(132,151,190,0.17)] dark:bg-[#101827] dark:text-[#F2F6FC] dark:shadow-[0_18px_44px_rgba(0,0,0,0.34),inset_0_1px_0_rgba(255,255,255,0.045)] dark:before:from-white/5 dark:before:via-transparent dark:after:bg-none';

export const roddoChipClass =
  'inline-flex items-center rounded-full border border-black/10 bg-white/80 px-3 py-1 text-[11px] font-extrabold text-[#56616D] shadow-[0_6px_14px_rgba(0,0,0,0.05)] dark:border-[rgba(132,151,190,0.17)] dark:bg-white/[0.045] dark:text-[#DCE3EE]';

export const roddoRewardChipClass =
  'inline-flex items-center rounded-full border border-[#E6C35A] bg-[#FFF0B8] px-3 py-1 text-[11px] font-extrabold text-[#6A5000] shadow-[0_5px_0_rgba(130,98,17,0.18)] dark:border-[#F2B84B]/25 dark:bg-[#F2B84B]/15 dark:text-[#F8D88E]';

export const roddoPageShellClass = 'text-[var(--roddo-current-text,var(--roddo-text-light))] dark:text-[var(--roddo-text-dark)]';

export const roddoPanelClass =
  'border border-[var(--roddo-current-border,var(--roddo-border-light))] bg-[var(--roddo-current-panel,var(--roddo-panel-light))] text-[var(--roddo-current-text,var(--roddo-text-light))] shadow-[var(--roddo-shadow-light)] dark:border-[var(--roddo-border-dark)] dark:bg-[var(--roddo-panel-dark)] dark:text-[var(--roddo-text-dark)] dark:shadow-[var(--roddo-shadow-dark)]';

export const roddoMenuPanelClass =
  'rounded-3xl border border-[var(--roddo-current-border,var(--roddo-border-light))] bg-[var(--roddo-current-panel-strong,var(--roddo-panel-light-strong))] text-[var(--roddo-current-text,var(--roddo-text-light))] backdrop-blur-2xl shadow-[0_16px_0_rgba(0,0,0,0.18)] dark:border-[var(--roddo-border-dark)] dark:bg-[var(--roddo-panel-dark-strong)] dark:text-[var(--roddo-text-dark)] dark:shadow-[0_16px_0_rgba(0,0,0,0.40)]';

export const roddoInputClass =
  'w-full rounded-2xl border border-[var(--roddo-current-border,var(--roddo-border-light))] bg-[var(--roddo-current-panel,var(--roddo-panel-light))] px-3 py-2.5 text-sm text-[var(--roddo-current-text,var(--roddo-text-light))] outline-none transition placeholder:text-black/40 focus:ring-2 focus:ring-[#7c6ff7]/30 dark:focus:ring-[#7657E8]/35 dark:border-[var(--roddo-border-dark)] dark:bg-[var(--roddo-panel-dark)] dark:text-[var(--roddo-text-dark)] dark:placeholder:text-white/40';
